import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Chip — para variant selector (talla, largo, color) y filtros.
 * Adaptado a "editorial brutalist": radios mínimos, sin shadow.
 */
const chipVariants = cva(
  'inline-flex items-center justify-center gap-1.5 rounded-xs border border-[var(--color-border)] font-[var(--font-body)] text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
  {
    variants: {
      size: {
        sm: 'h-7 px-3 text-xs',
        md: 'h-10 px-5 text-sm',
        lg: 'h-12 px-6 text-base',
      },
      state: {
        default:
          'text-[var(--color-fg)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] cursor-pointer',
        selected:
          'border-[1.5px] border-[var(--color-accent)] bg-[var(--color-accent)]/8 text-[var(--color-accent)]',
        disabled:
          'cursor-not-allowed border-[var(--color-border)] text-[var(--color-fg-subtle)] opacity-50 line-through',
      },
    },
    defaultVariants: {
      size: 'md',
      state: 'default',
    },
  }
);

export interface ChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    VariantProps<typeof chipVariants> {
  disabled?: boolean;
}

const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ className, size, state, disabled, ...props }, ref) => {
    const effectiveState = disabled ? 'disabled' : state;
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        aria-pressed={state === 'selected' ? 'true' : undefined}
        className={cn(chipVariants({ size, state: effectiveState, className }))}
        {...props}
      />
    );
  }
);
Chip.displayName = 'Chip';

export { Chip, chipVariants };
